import { AxiosError } from "axios";
import dayjs from "dayjs";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import { buildHierarchyStatusFilter } from "../../home/utils/serialization";

export const getCodeError = (error: unknown) => {
  if (error instanceof AxiosError) {
    const errors = error.response?.data?.errors;
    if (errors?.code && Array.isArray(errors.code) && errors.code.length > 0) {
      return errors.code[0];
    }
  }
};

/**
 * Filters formality requests based on a search string
 * Searches across name, code, status, applicant name and email
 *
 * @param requestList - Array of formality requests to filter
 * @param searchString - Search term to filter by
 * @returns Filtered array of formality requests
 */
export const filterFormalityRequests = <
  T extends {
    name?: string | null;
    code?: string | null;
    status?: string | null;
    updated_at?: string | null;
    applicant?: {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    } | null;
  },
>(
  requestList: T[],
  searchString?: string,
  queryFilters?: object,
): T[] => {
  if (queryFilters && Object.keys(queryFilters).length > 0) {
    // filter by status and date
    if (
      "status" in queryFilters &&
      typeof queryFilters["status"] === "string" &&
      queryFilters["status"].trim() !== ""
    ) {
      const validStatus = new Set(
        queryFilters["status"]
          .split("|")
          .flatMap((group) => {
            const [, children] = group.split(":");
            if (!children) return [];
            return children.split(",");
          })
          .map((s) => s.trim()),
      );

      requestList = requestList.filter(
        (request) => request.status && validStatus.has(request.status),
      );
    }

    // Handle date range filters - expect updated_at with format "from,to"
    if (
      "updated_at" in queryFilters &&
      typeof queryFilters["updated_at" as keyof typeof queryFilters] ===
        "string"
    ) {
      const dateValue = queryFilters[
        "updated_at" as keyof typeof queryFilters
      ] as string;
      const dateRange = dateValue.split(",").map((d) => d.trim());

      requestList = requestList.filter((request) => {
        if (!request.updated_at) return false;
        const updatedAt = dayjs(request.updated_at);

        if (dateRange.length === 2) {
          // Both from and to - filter by range (inclusive)
          const startDate = dayjs(dateRange[0]);
          const endDate = dayjs(dateRange[1]);
          return (
            (updatedAt.isAfter(startDate) ||
              updatedAt.isSame(startDate, "day")) &&
            (updatedAt.isBefore(endDate) || updatedAt.isSame(endDate, "day"))
          );
        } else if (dateRange.length === 1 && dateRange[0]) {
          // Single date - exact match
          const date = dayjs(dateRange[0]);
          return (
            updatedAt.isSame(date, "day") || updatedAt.isAfter(date, "day")
          );
        }
        return true;
      });
    }
  }
  if (!searchString || searchString.trim() === "") {
    return requestList;
  }

  const searchLower = searchString.toLowerCase().trim();
  return requestList.filter((request) => {
    // Buscar en nombre del trámite
    if (request.name?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en código/ID
    if (request.code?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en estado
    if (request.status?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en nombre del solicitante
    const applicantFullName =
      `${request.applicant?.first_name ?? ""} ${request.applicant?.last_name ?? ""}`.toLowerCase();
    if (applicantFullName.includes(searchLower)) {
      return true;
    }

    // Buscar en email del solicitante
    if (request.applicant?.email?.toLowerCase().includes(searchLower)) {
      return true;
    }

    return false;
  });
};

/**
 * Sort formality requests by a specific field
 * @param requestList - Array of formality requests to sort
 * @param columnId - Column ID to sort by
 * @param descending - Whether to sort in descending order
 * @returns Sorted array of formality requests
 */
export const sortFormalityRequests = <
  T extends {
    name?: string | null;
    code?: string | null;
    status?: string | null;
    created_at?: string | null;
    applicant?: {
      first_name?: string | null;
      last_name?: string | null;
    } | null;
  },
>(
  requestList: T[],
  columnId: string,
  descending: boolean,
): T[] => {
  return [...requestList].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (columnId) {
      case "name":
        aValue = a.name?.toLowerCase() ?? "";
        bValue = b.name?.toLowerCase() ?? "";
        break;
      case "code":
        aValue = a.code ?? "";
        bValue = b.code ?? "";
        break;
      case "status":
        aValue = a.status?.toLowerCase() ?? "";
        bValue = b.status?.toLowerCase() ?? "";
        break;
      case "applicant":
        aValue = a.applicant?.first_name?.toLowerCase() ?? "";
        bValue = b.applicant?.first_name?.toLowerCase() ?? "";
        break;
      case "elapsed_days":
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return descending ? 1 : -1;
    if (aValue > bValue) return descending ? -1 : 1;
    return 0;
  });
};

/**
 * Sort resolutions by a specific field
 * @param resolutionList - Array of resolutions to sort
 * @param columnId - Column ID to sort by
 * @param descending - Whether to sort in descending order
 * @returns Sorted array of resolutions
 */
export const sortResolutions = <
  T extends {
    display_name?: string | null;
    display_code?: string | null;
    display_date?: string | null;
    description?: string | null;
    formality_request?: {
      name?: string | null;
    } | null;
  },
>(
  resolutionList: T[],
  columnId: string,
  descending: boolean,
): T[] => {
  return [...resolutionList].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (columnId) {
      case "name":
        aValue = a.display_name?.toLowerCase() ?? "";
        bValue = b.display_name?.toLowerCase() ?? "";
        break;
      case "display_code":
        aValue = a.display_code ?? "";
        bValue = b.display_code ?? "";
        break;
      case "formality_request":
        aValue =
          a.formality_request?.name?.toLowerCase() ??
          a.description?.toLowerCase() ??
          "";
        bValue =
          b.formality_request?.name?.toLowerCase() ??
          b.description?.toLowerCase() ??
          "";
        break;
      case "elapsed_days":
        aValue = a.display_date ? new Date(a.display_date).getTime() : 0;
        bValue = b.display_date ? new Date(b.display_date).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return descending ? 1 : -1;
    if (aValue > bValue) return descending ? -1 : 1;
    return 0;
  });
};

export const getDefaultDetailsFilters = (
  status: string,
  isResolution: boolean,
) => {
  if (isResolution) {
    return [
      {
        id: 1,
        field: "updated_at",
        type: filterTypesEnum.dates,
        label: "Última actualización",
        shouldDisabledFutureDates: true,
        isUpdated: false,
        result: "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado",
        isUpdated: false,
        result: status,
        status: [
          {
            field: "APPROVED",
            label: "Aprobado",
            value: status.includes("APPROVED"),
          },
          {
            field: "DENIED",
            label: "Denegado",
            value: status.includes("DENIED"),
          },
          {
            field: "UNFAIR",
            label: "Improcedente",
            value: status.includes("UNFAIR"),
          },
          {
            field: "CANCELED",
            label: "Desistido",
            value: status.includes("CANCELED"),
          },
        ],
      },
    ];
  }
  return [
    {
      id: 1,
      field: "updated_at",
      type: filterTypesEnum.dates,
      label: "Última actualización",
      shouldDisabledFutureDates: true,
      isUpdated: false,
      result: "",
    },
    buildHierarchyStatusFilter(status) as FilterOption,
  ];
};
